/**
 * Exports Page - Step 5 of 5-Step Workflow
 *
 * Export prices to external systems (Square, QuickBooks, Shopify) or download files.
 * Positioned as the final step in Rocket Pricing's workflow:
 * Data → Costs → Rules → Prices → [Exports]
 */

import { useState } from 'react'
import { Tabs, Typography, Spin } from 'antd'
import {
  DashboardOutlined,
  CloudSyncOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { NoOrgSelectedPrompt } from '@/components/NoOrgSelectedPrompt'
import { OverviewTab, ConnectedExportsTab, ManualDownloadsTab, ExportHistoryTab } from './components'

const { Title } = Typography

type TabKey = 'overview' | 'connected' | 'manual' | 'history'

export default function ExportsPage() {
  // Check for org context to prevent RLS errors
  const { data: user, isLoading: isUserLoading } = useCurrentUser()
  const hasOrgContext = !!user?.currentOrganization

  const [currentTab, setCurrentTab] = useState<TabKey>('overview')

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <DashboardOutlined />
          Overview
        </span>
      ),
      children: <OverviewTab onTabChange={(tab) => setCurrentTab(tab as TabKey)} />,
    },
    {
      key: 'connected',
      label: (
        <span>
          <CloudSyncOutlined />
          Connected Exports
        </span>
      ),
      children: <ConnectedExportsTab />,
    },
    {
      key: 'manual',
      label: (
        <span>
          <DownloadOutlined />
          Manual Downloads
        </span>
      ),
      children: <ManualDownloadsTab />,
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined />
          Export History
        </span>
      ),
      children: <ExportHistoryTab />,
    },
  ]

  // Loading state
  if (isUserLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  // No organization selected - show friendly prompt
  if (!hasOrgContext) {
    return <NoOrgSelectedPrompt featureName="exports" />
  }

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Exports
      </Title>

      <Tabs
        activeKey={currentTab}
        onChange={(key) => setCurrentTab(key as TabKey)}
        items={tabItems}
        destroyOnHidden
      />
    </div>
  )
}
